import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { ClockIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

// Staff-facing time clock. Big-button UI with live elapsed timer, today's
// shifts list, and inline feedback if the system applied a late_arrival or
// long-lunch point.

function formatElapsed(startISO) {
  const ms = Date.now() - new Date(startISO).getTime();
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
}

const EVENT_LABEL = {
  clock_in: 'Clocked in',
  clock_out: 'Clocked out',
  late_arrival: '🟡 Late arrival (+0.5)',
  late_return_from_break: '🟡 Long lunch (+0.5)',
};

export default function ClockPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now()); // ticks every second for elapsed display
  // The kiosk QR encodes /clock?kt=<token>. Stash it so we can pass it on
  // the next clock-in/out call as proof of physical presence, then strip it
  // from the URL so a refresh doesn't re-fire.
  const kioskTokenRef = useRef(searchParams.get('kt'));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/me/clock-status');
      setStatus(data);
    } catch (err) {
      // Special-case 401 so the teacher gets an actionable message
      // instead of a mystery kick-out. The api client no longer auto-
      // logs out on clock paths (see api/client.js) to break the
      // scan → 401 → login → scan → 401 loop.
      if (err.response?.status === 401) {
        toast.error('Your session expired. Tap the menu → Sign Out, then sign back in. You will NOT be kicked back here again.', { duration: 10000 });
      } else {
        toast.error(err.response?.data?.error || 'Failed to load clock status');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!status?.clocked_in) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status?.clocked_in]);

  // `now` is intentionally included so the elapsed clock ticks every second
  // when clocked in. Computed inline rather than via useMemo to keep the
  // dependency story trivial.
  const elapsed = status?.clocked_in ? formatElapsed(status.current_shift_started_at) : null;
  // Mark `now` as read so eslint-unused-vars doesn't complain (the setInterval
  // call updates it; the inline expression above reads it indirectly via Date.now).
  void now;

  // Build body with the one-shot kiosk token (if we have one).
  //
  // This deliberately does NOT clear the token — clearKioskToken() does that,
  // on the SUCCESS path only. The clear used to live here, i.e. it ran BEFORE
  // the POST and therefore ran on failure too: the first tap sent the token
  // and got "Kiosk QR code expired", and every tap after that sent an empty
  // body and got "Scan the kiosk QR on the tablet" — a different error, for a
  // token the page had silently thrown away. Once you failed once, retrying
  // could not succeed no matter how many times you rescanned.
  const kioskBody = () => {
    const token = kioskTokenRef.current;
    return token ? { kiosk_token: token } : {};
  };

  // Burn the token once it has actually been ACCEPTED, so a refresh or the
  // back button can't replay the action. That replay protection is why the
  // clear existed at all; it just used to fire whether the request succeeded
  // or not. Leaving the token (and ?kt=) in place after a failure is what
  // makes a retry able to work.
  const clearKioskToken = () => {
    if (!kioskTokenRef.current) return;
    kioskTokenRef.current = null;
    setSearchParams({}, { replace: true });
  };

  const doClockIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/me/clock-in', kioskBody());
      clearKioskToken();
      if (data.late_arrival) toast(`Clocked in. Note: ${data.late_arrival.minutes_late} min late — 0.5 attendance point added.`, { duration: 6000 });
      else if (data.late_return_from_break) toast(`Clocked back in. Lunch break exceeded 65 min — 0.5 attendance point added.`, { duration: 6000 });
      else toast.success('Clocked in.');
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Your session expired. Tap the menu → Sign Out, then sign back in. You will NOT be kicked back here again.', { duration: 10000 });
      } else {
        toast.error(err.response?.data?.error || 'Clock-in failed.');
      }
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/me/clock-out', kioskBody());
      clearKioskToken();
      toast.success(`Clocked out. ${Math.floor(data.minutes_worked / 60)}h ${data.minutes_worked % 60}m on the clock.`);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Your session expired. Sign out and sign back in.', { duration: 10000 });
      } else {
        toast.error(err.response?.data?.error || 'Clock-out failed.');
      }
    } finally { setBusy(false); }
  };

  // When the staff arrives via a kiosk QR scan, automatically fire the right
  // action based on current status — saves a tap on a sleep-deprived phone.
  // Only auto-fires once: after that the buttons take over.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (loading || busy) return;
    if (!kioskTokenRef.current) return;
    if (!status) return;
    autoFiredRef.current = true;
    if (status.clocked_in) doClockOut();
    else doClockIn();
  }, [loading, busy, status, doClockIn, doClockOut]);

  // navigate import kept for future deep-link expansion (e.g. break flows)
  void navigate;

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up max-w-2xl mx-auto">
      <Toaster position="top-right" />

      <h1 className="text-2xl font-bold text-gray-900 mb-1">Time Clock</h1>
      <p className="text-gray-500 text-sm mb-6">
        Hi {user?.first_name}. {status?.scheduled_shift
          ? `Your shift today: ${status.scheduled_shift.start_time?.slice(0, 5)} – ${status.scheduled_shift.end_time?.slice(0, 5)}.`
          : 'No scheduled shift on file for today.'}
      </p>

      {/* Big status card */}
      <div className={`rounded-3xl border-2 p-8 text-center mb-6 ${
        status?.clocked_in
          ? 'bg-green-50 border-green-300'
          : 'bg-gray-50 border-gray-200'
      }`}>
        {status?.clocked_in ? (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 mb-4">
              <ClockIcon className="w-9 h-9 text-white" />
            </div>
            <p className="text-green-700 font-semibold text-sm mb-1">CURRENTLY CLOCKED IN</p>
            <p className="text-gray-500 text-xs">Since {formatTime(status.current_shift_started_at)}</p>
            <p className="text-5xl font-bold tracking-tight text-gray-900 my-4 font-mono">{elapsed}</p>
            <button
              onClick={doClockOut}
              disabled={busy}
              className="bg-red-500 hover:bg-red-600 disabled:bg-red-500/40 text-white font-bold px-8 py-3 rounded-2xl text-lg inline-flex items-center gap-2 shadow-lg shadow-red-500/25"
            >
              <ArrowLeftOnRectangleIcon className="w-6 h-6" />
              {busy ? 'Clocking out…' : 'Clock Out'}
            </button>
          </>
        ) : (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-300 mb-4">
              <ClockIcon className="w-9 h-9 text-white" />
            </div>
            <p className="text-gray-700 font-semibold text-sm mb-1">NOT CLOCKED IN</p>
            <p className="text-gray-500 text-xs mb-5">
              {status?.todays_events?.length > 0
                ? "You've already worked earlier today — clock back in for your next shift."
                : "Tap below to clock in for the day."}
            </p>
            <button
              onClick={doClockIn}
              disabled={busy}
              className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-bold px-8 py-3 rounded-2xl text-lg shadow-lg shadow-orange-500/25"
            >
              {busy ? 'Clocking in…' : 'Clock In'}
            </button>
          </>
        )}
      </div>

      {/* Today's events */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-gray-400" /> Today
        </h2>
        {(!status?.todays_events || status.todays_events.length === 0) ? (
          <p className="text-sm text-gray-500">No events yet today.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {status.todays_events.map(ev => (
              <li key={ev.id} className="flex items-center justify-between border-b border-gray-50 pb-1.5">
                <span className="text-gray-700">{EVENT_LABEL[ev.event_type] || ev.event_type}</span>
                <span className="text-gray-500 text-xs font-mono">{formatTime(ev.occurred_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Rules reminder */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-xs text-amber-800 flex items-start gap-2">
        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium mb-1">Quick rules:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Clock in up to 10 min before your shift starts — earlier is blocked.</li>
            <li>Clocking in more than 6 min late adds 0.5 attendance points.</li>
            <li>Lunch break over 65 min adds 0.5 attendance points.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
